import type { ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useNavigation, Outlet } from 'react-router-dom'
import { Navbar } from '@/features/navigation/components/Navbar'
import { PageSpinningLoader, RawQueryState } from '@joyful/ui/core'
import {
  GlobalCommandProvider,
  JoyfulWebAppLayout,
  useDisplaySettings,
  PageTitle,
  reportCaughtError,
} from '@joyful/ui/app'
import { ErrorRouteFallback } from '@/routes/ErrorRoute'
import { useSetActiveAccount } from '@/features/auth/hooks/useSetActiveAccount'
import { useAppNavigationItems } from '@/features/navigation/hooks/useAppNavigationItems'

export function PageLayout() {
  const { state: navigationState } = useNavigation()

  return (
    <AppWebGlobalCommandProvider>
      <JoyfulWebAppLayout
        header={
          <>
            <PageTitle />
            <Navbar />
          </>
        }
      >
        <RawQueryState
          isPending={navigationState === 'loading'}
          isError={false}
          isSuccess
          loading={<PageSpinningLoader />}
        >
          <ErrorBoundary
            FallbackComponent={ErrorRouteFallback}
            onError={(error, errorInfo) => {
              reportCaughtError({ error, errorInfo })
            }}
          >
            <Outlet />
          </ErrorBoundary>
        </RawQueryState>
      </JoyfulWebAppLayout>
    </AppWebGlobalCommandProvider>
  )
}

export interface AppWebGlobalCommandProviderProps {
  readonly children: ReactNode
}

export function AppWebGlobalCommandProvider({
  children,
}: AppWebGlobalCommandProviderProps) {
  const [displaySettings] = useDisplaySettings()
  const setActiveAccount = useSetActiveAccount()
  const appNavigationItems = useAppNavigationItems()
  const isDemoModeActive =
    displaySettings.demo_mode?.account_slug !== null &&
    displaySettings.demo_mode?.account_slug !== undefined

  return (
    <GlobalCommandProvider
      accountNavigationItems={appNavigationItems.expanded.account}
      activeApp="app"
      generalNavigationItems={appNavigationItems.expanded.general}
      onSelectAccount={
        isDemoModeActive
          ? undefined
          : (account) => {
              setActiveAccount.mutate({ account })
            }
      }
      routeNavigationSections={['primary', 'account']}
    >
      {children}
    </GlobalCommandProvider>
  )
}
